import { PermissionEnum } from '@electricjs/core_entity-client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Chip,
  ColorsV2,
  ColumnVisibilityMenu,
  Menu,
  MenuItem,
  Text,
  useTabContext,
} from '@electricjs/arc';

import TableHeaderBar from 'common/TableToolbar/TableHeaderBar';
import { useUserHasOrganizationPermission } from '@/hooks/useUserHasOrganizationPermission';
import { ModalParamKeys, NavigationParamKeys } from '@/types/queryParamKeys';
import {
  AssetManagementTabs,
  AssetManagementTabType,
} from '@/pages/Assets/types';
import React, { Dispatch } from 'react';

type AssetsTableHeaderProps = {
  totalAssets: number;
  handleExport: () => void;
  isExportingAssets: boolean;
  columnVisibility: { [key: string]: boolean };
  setColumnVisibility: Dispatch<
    React.SetStateAction<{
      [key: string]: boolean;
    }>
  >;
  columns: { id: string; label: string }[];
};

const AssetsTableHeader = ({
  totalAssets,
  handleExport,
  isExportingAssets,
  columnVisibility,
  setColumnVisibility,
  columns,
}: AssetsTableHeaderProps) => {
  const { t } = useTranslation(['assets', 'components']);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedId: selectedTab } = useTabContext();

  const tableTitleMap = {
    [AssetManagementTabs.ASSIGNED]: t('assetsPage.assignedAssets'),
    [AssetManagementTabs.UNASSIGNED]: t('assetsPage.unassignedAssets'),
    [AssetManagementTabs.ARCHIVED]: t('assetsPage.archivedAssets'),
  };

  const canCreateAssets = useUserHasOrganizationPermission(
    PermissionEnum.Assetscreate
  );

  // These parameters are used to open the CSV upload modal in @/pages/Assets/Assets.tsx
  const handleOpenModal = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(NavigationParamKeys.Modal, ModalParamKeys.AssetsCsvUpload);
    setSearchParams(newParams, { replace: true });
  };

  const amountText = (amount: number) =>
    amount !== 1 ? t('assetsPage.assets') : t('assetsPage.asset');

  const title = (
    <Text variant="heading-2">
      {selectedTab
        ? tableTitleMap[selectedTab as AssetManagementTabType]
        : t('assetsPage.assets')}
    </Text>
  );

  return (
    <TableHeaderBar
      title={title}
      chips={
        <Chip
          id="assets-count-chip"
          backgroundColor={ColorsV2.PRIMARY_LIGHTEST}
          variant="small">
          <Text variant="legal" intent="brand">
            {`${totalAssets} ${amountText(totalAssets)}`}
          </Text>
        </Chip>
      }
      actions={
        canCreateAssets && (
          <>
            <ColumnVisibilityMenu
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
              columns={columns}
              translations={{
                columnsTitle: t('components:columnVisibilityMenu.columnsTitle'),
                customizeColumnsLabel: t(
                  'components:columnVisibilityMenu.customizeColumnsLabel'
                ),
                doneLabel: t('components:columnVisibilityMenu.doneLabel'),
                hiddenColumnsText: t(
                  'components:columnVisibilityMenu.hiddenColumnsText',
                  {
                    count: Object.values(columnVisibility).filter(v => !v)
                      .length,
                  }
                ),
              }}
            />
            <Button
              id={`export-assets-button-${selectedTab}`}
              variant="outline"
              iconBefore="download"
              loading={isExportingAssets}
              onClick={handleExport}>
              {t('assetsPage.download')}
            </Button>
            <Menu
              placement="bottom-start"
              overflowContainer
              trigger={
                <Button
                  id={`add-assets-button-${selectedTab}`}
                  iconAfter="chevron-down">
                  {t('assetsPage.addAsset')}
                </Button>
              }>
              <MenuItem
                id={`add-assets-${selectedTab}`}
                onClick={() => navigate(`/assets/new`)}>
                <Text>{t('assetsPage.manual')}</Text>
              </MenuItem>
              <MenuItem
                id={`import-assets-${selectedTab}`}
                onClick={handleOpenModal}>
                <Text>{t('assetsPage.importCSV')}</Text>
              </MenuItem>
            </Menu>
          </>
        )
      }
    />
  );
};

export default AssetsTableHeader;
